// @ts-strict-ignore
import {
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
  ElementRef,
  NgZone,
  ChangeDetectionStrategy,
} from '@angular/core';
import { LMFormRequestService } from '@app/components/form';
import {
  SohoBusyIndicatorDirective,
  SohoDataGridComponent,
  SohoModalDialogRef,
  SohoModalDialogService,
} from 'ids-enterprise-ng';
import { first } from 'rxjs/operators';
import { LMCreateReportSearchListDialogComponent } from '../search-list-dialog/lm-create-report-search-list-dialog.component';
import { Subject } from 'rxjs';
import {
  FormRequestType,
  LMDialogButtonKey,
  LMUniqueIdService,
} from '@app/utils';
import { LMFormatUtilService, LMUrlUtilService } from '@app/services';
import { LMCreateReportDialogService } from '@report/index';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';

interface BusinessClass {
  value: string;
  label: string;
}

interface ListName {
  name: string;
  isPrimary?: boolean;
  isCubeView?: boolean;
}

@Component({
  selector: 'lm-create-report-dialog-from-banner',
  templateUrl: './lm-create-report-dialog.component.html',
  styleUrls: ['./lm-create-report-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  })
export class LMCreateReportDialogComponent implements OnInit, OnDestroy {
  @ViewChild(SohoBusyIndicatorDirective, { static: true })
  busyIndicator: SohoBusyIndicatorDirective;
  @ViewChild(SohoDataGridComponent, { static: false })
  datagrid: SohoDataGridComponent;
  @Input() clickContinue$;

  createReportForm: FormGroup;
  createReportShowMain = true;
  submitted = false;

  businessClasses: BusinessClass[] = [];
  listNames: ListName[] = [];
  userFolders: IReport[] = [];
  url: string;

  columns: SohoDataGridColumn[] = [
    {
      id: 'selectionCheckbox',
      sortable: false,
      resizable: false,
      formatter: Soho.Formatters.SelectionCheckbox,
      align: 'center',
    },
    {
      id: 'field',
      name: Soho.Locale.translate('ListLabel'),
      sortable: false,
      field: 'label',
      textOverflow: 'ellipsis',
    },
    {
      id: 'field',
      name: Soho.Locale.translate('ListField'),
      sortable: false,
      field: 'field',
      textOverflow: 'ellipsis',
    },
  ];

  gridOptions = <SohoDataGridOptions>{
    columns: this.columns,
    rowHeight: 'extra-small',
    isList: true,
    clickToSelect: true,
    selectable: 'mixed',
    showSelectAllCheckBox: true,
  };

  titleChanged$ = new Subject<string>();
  listNameChanged$ = new Subject<string>();

  businessClassAutomationId = [{ name: 'data-automation-id', value: 'lm-create-report-business-class'}];

  constructor(
    private ngZone: NgZone,
    private lmFormRequestService: LMFormRequestService,
    private sohoModalDialogService: SohoModalDialogService,
    private lmUniqueIdService: LMUniqueIdService,
    private lmFormatUtilService: LMFormatUtilService,
    private lmUrlUtilService: LMUrlUtilService,
    private changeDetectionRef: ChangeDetectorRef,
    private lmCreateReportDialogService: LMCreateReportDialogService,
    private elementRef: ElementRef,
    private formBuilder: FormBuilder
  ) {}

  ngOnInit() {
    this.createReportForm = this.formBuilder.group({
      businessClass: ['', Validators.required],
      listName: [''],
      reportType: ['ReportCopiedFromReport', Validators.required],
      reportName: ['', Validators.required],
      title: [
        '',
        [Validators.required, this.validateUserFolderTyped.bind(this)],
      ],
      createReportUrl: [this.url],
      userFolderId: [''],
      isCubeView: [false],
    });

    this.createReportForm.valueChanges.subscribe(() => {
      this.changeDetectionRef.detectChanges();
    });

    this.loadBusClasses();
    this.createReportShowMain = true;
    // template
    this.clickContinue$.subscribe(() => {
      this.submitted = true;
      if (this.createReportForm.valid) {
        if (this.isCubeView.value) {
          this.lmCreateReportDialogService.setCreateReportFields(this.myForm);
          return;
        }

        this.createReportShowMain = !this.createReportShowMain;    
        this.lmCreateReportDialogService.showMainContainer =
          this.createReportShowMain;
        const cancelButton =
          this.elementRef.nativeElement.parentElement.parentElement.parentElement.querySelector(
            `button[id="CreateReportFromBanner_Cancel"]`
          );
        const title =
          this.elementRef.nativeElement.parentElement.parentElement.parentElement.querySelector(
            `h1[id="h1-title"]`
          );
        if (this.createReportShowMain) {
          // temporary, will create soho ticket for this
          cancelButton.innerText = Soho.Locale.translate('Cancel');
          title.innerText = Soho.Locale.translate('CreateReport');
        } else {
          cancelButton.innerText = Soho.Locale.translate('Back');
          title.innerText = Soho.Locale.translate('ReviewAndEditFields');
          this.lmCreateReportDialogService.setCreateReportFields(this.myForm);
        }
      }
      this.changeDetectionRef.detectChanges();
    });
  }

  loadBusClasses() {
    this.busyIndicator.activated = true;
    this.lmFormRequestService
      .getBusClasses(this.url)
      .pipe(first())
      .subscribe(
        (busClasses: any) => {
          this.businessClasses = busClasses.map(({ name }) => ({
            value: name,
            label: name,
          }));
          const defaultBusinessClass = this.businessClasses?.[0].value;
          this.busClass.setValue(defaultBusinessClass);
          this.changeDetectionRef.detectChanges();
          this.onBusClassChange();
          this.busyIndicator.activated = false;
        },
        (err: any) => {
          this.busyIndicator.activated = false;
        }
      );
  }

  changeReportType() {
    this.submitted = false;
    this.listName.clearValidators();
    this.title.clearValidators();
    this.createReportForm.markAsPristine();

    if (this.reportType.value === 'ReportCopiedFromReport') {
      this.title.setValidators([
        Validators.required,
        this.validateUserFolderTyped.bind(this),
      ]);
    } else {
      this.listName.setValidators([
        Validators.required,
        this.validateListNameTyped.bind(this),
      ]);
    }

    this.createReportForm.updateValueAndValidity();
    this.onBusClassChange();
    this.changeDetectionRef.detectChanges();
  }

  onBusClassChange() {
    if (this.reportType.value === 'ReportCopiedFromReport') {
      this.loadReportNames();
    } else if (this.reportType.value === 'ReportCopiedFromList') {
      this.loadListNames();
    } else {
      this.loadPrimaryList();
    }
    this.userFolderId.reset();
    this.title.reset();
    this.listName.reset();
  }

  loadPrimaryList() {
    this.busyIndicator.activated = true;
    this.lmFormRequestService
      .getPrimaryList(
        this.lmUrlUtilService.getReportUrl(this.busClass.value),
        FormRequestType.PrimaryList,
        this.busClass.value
      )
      .pipe(first())
      .subscribe(
        (listSpec: any) => {
          this.listNames = [listSpec];
          this.listName.setValue(listSpec.name);
          this.isCubeView.setValue(!!listSpec.isCubeView);
          this.busyIndicator.activated = false;
        },
        (err: any) => {
          this.busyIndicator.activated = false;
        }
      );
  }

  loadListNames() {
    this.busyIndicator.activated = true;
    this.lmFormRequestService
      .getListDetails(
        this.lmUrlUtilService.getReportUrl(this.busClass.value),
        FormRequestType.ListNamesForCreateReport,
        this.busClass.value,
        {}
      )
      .pipe(first())
      .subscribe(
        (listNames: any) => {
          this.listNames = listNames;

          this.busyIndicator.activated = false;
        },
        (err: any) => {
          this.busyIndicator.activated = false;
        }
      );
  }

  loadReportNames() {
    this.lmFormRequestService
      .getListDetails(
        this.lmUrlUtilService.getReportUrl(this.busClass.value),
        FormRequestType.ReportNamesForCreateReport,
        this.busClass.value
      )
      .pipe(first())
      .subscribe(
        (userFolders: IReport[]) => {
          this.userFolders = userFolders;
          this.busyIndicator.activated = false;
        },
        (err: any) => {
          this.busyIndicator.activated = false;
        }
      );
  }

  public listLookupClick($event: any, sohoLookup: any, clickArguments: any) {
    // let dialog: LMCreateReportSearchListDialogComponent;
    // eslint-disable-next-line prefer-const
    let dialogRef: SohoModalDialogRef<LMCreateReportSearchListDialogComponent>;
    const dialogId = 'CreateReportSearchList';

    const reportType = clickArguments.reportType;
    const createReportUrl = clickArguments.createReportUrl;
    const businessClass = clickArguments.businessClass;

    const component = clickArguments.component;
    const buttons: Array<SohoModalButton> = [
      {
        id: component.lmUniqueIdService.uniqueId([
          dialogId,
          LMDialogButtonKey.CANCEL,
        ]),
        text: Soho.Locale.translate(LMDialogButtonKey.CANCEL),
        click: () => {
          dialogRef.close();
        },
      },
      {
        id: component.lmUniqueIdService.uniqueId([
          dialogId,
          LMDialogButtonKey.OK,
        ]),
        text: Soho.Locale.translate(LMDialogButtonKey.OK),
        click: () => {
          const rows = dialogRef.componentDialog.datagrid.selectedRows();
          if (rows.length > 0) {
            const firstRow = rows[0]?.data;
            component.myForm['listName'].setValue(firstRow?.name);
            component.myForm['isCubeView'].setValue(!!firstRow.isCubeView);
            if (reportType === 'ReportCopiedFromReport') {
              component.myForm['userFolderId'].setValue(firstRow?.userFolderId);
              sohoLookup.element.val(firstRow?.title).trigger('change').focus();
            } else {
              sohoLookup.element.val(firstRow?.name).trigger('change').focus();
            }
          }
          dialogRef.close();
        },
      },
    ];

    const dialogTitle = component.lmFormatUtilService.formatMessage(
      Soho.Locale.translate(
        reportType === 'ReportCopiedFromList'
          ? 'FindYourList'
          : 'FindYourReport'
      ),
      [businessClass]
    );

    const attributes: SohoAttributesArray = [ {
      name: 'data-automation-id',
      value: `lm-create-report-list-modal`
    } ];

    dialogRef = component.sohoModalDialogService.modal(LMCreateReportSearchListDialogComponent, null, { attributes })
      .title(dialogTitle)
      .buttons(buttons)
      .isAlert(false)
      .apply((comp: LMCreateReportSearchListDialogComponent) => {
        const dialog = comp;
        dialog.reportType = reportType;
        dialog.url = createReportUrl;
        dialog.businessClass = businessClass;
      })
      .open();
  }

  validateUserFolderTyped() {
    const matchedUserFolder = this.userFolders.find((userFolder: IReport) => {
      if (userFolder.title.toLowerCase() === this.title.value?.toLowerCase()) {
        this.isCubeView.setValue(!!userFolder.isCubeView);
        this.userFolderId.setValue(userFolder.userFolderId);
        this.listName.setValue(userFolder.name);
        return true;
      }
    });

    return !matchedUserFolder ? { invalidReportName: true } : null;
  }

  validateListNameTyped() {
    const matchedListName = this.listNames.find((listName) => {
      if (listName.name.toLowerCase() === this.listName.value?.toLowerCase()) {
        this.isCubeView.setValue(!!listName.isCubeView);
        // this.listName.setValue(listName.name);
        return true;
      }
    });

    return !matchedListName ? { invalidListName: true } : null;
  }

  get myForm() {
    return this.createReportForm.controls;
  }

  get busClass() {
    return this.myForm['businessClass'] as FormControl;
  }

  get reportName() {
    return this.myForm['reportName'] as FormControl;
  }

  get listName() {
    return this.myForm['listName'] as FormControl;
  }

  get title() {
    return this.myForm['title'] as FormControl;
  }

  get reportType() {
    return this.myForm['reportType'] as FormControl;
  }

  get userFolderId() {
    return this.myForm['userFolderId'] as FormControl;
  }

  get createReportUrl() {
    return this.myForm['createReportUrl'] as FormControl;
  }

  get isCubeView() {
    return this.myForm['isCubeView'] as FormControl;
  }

  ngOnDestroy() {
    this.titleChanged$.complete();
    this.listNameChanged$.complete();
    this.clickContinue$.complete();
    this.lmCreateReportDialogService.showMainContainer = true;
  }
}
